import { useState, useEffect, useRef } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiUsers, FiUpload, FiDownload, FiUserPlus, FiUserCheck } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';

export default function Classes() {
  const { user, tenant } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isSoloMode = tenant?.operationType === 'solo';
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showStudentsModal, setShowStudentsModal] = useState(false);
  const [showTeachersModal, setShowTeachersModal] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState([]);
  const [uploadResult, setUploadResult] = useState(null);  // { success: N, failed: N, errors: [] }
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    grade: '',
    schedule: [],
    maxStudents: 20,
    tuitionFee: 0,
    teachers: []
  });
  const [scheduleErrors, setScheduleErrors] = useState([]);

  const days = ['월', '화', '수', '목', '금', '토', '일'];

  useEffect(() => {
    loadClasses();
    loadStudents();
    loadTeachers();
  }, []);

  const loadClasses = async () => {
    try {
      const { data } = await api.get('/api/class-groups', { params: { includeStudents: 'true' } });
      setClasses(data.classGroups || []);
    } catch (error) {
      console.error('Failed to load classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStudents = async () => {
    try {
      const { data } = await api.get('/api/users', { params: { role: 'student' } });
      setStudents(data.users || []);
    } catch (error) {
      console.error('Failed to load students:', error);
    }
  };

  const loadTeachers = async () => {
    try {
      const { data } = await api.get('/api/users', { params: { role: 'teacher' } });
      setTeachers(data.users || []);
    } catch (error) {
      console.error('Failed to load teachers:', error);
    }
  };

  const openModal = (cls = null) => {
    if (cls) {
      setEditingClass(cls);
      setFormData({
        name: cls.name,
        subject: cls.subject || '',
        grade: cls.grade || '',
        schedule: Array.isArray(cls.schedule) ? cls.schedule : [],
        maxStudents: cls.maxStudents || 20,
        tuitionFee: cls.tuitionFee || 0,
        teachers: cls.teachers?.map(t => t._id || t) || []
      });
    } else {
      setEditingClass(null);
      setFormData({
        name: '',
        subject: '',
        grade: '',
        schedule: [],
        maxStudents: 20,
        tuitionFee: 0,
        teachers: []
      });
    }
    setScheduleErrors([]);
    setShowModal(true);
  };

  const openTeachersModal = (cls) => {
    setSelectedClass(cls);
    setShowTeachersModal(true);
  };

  const openStudentsModal = (cls) => {
    setSelectedClass(cls);
    setShowStudentsModal(true);
  };

  // Check if two time ranges overlap
  const timesOverlap = (start1, end1, start2, end2) => {
    return start1 < end2 && start2 < end1;
  };

  // Validate schedule: check time order and overlapping times on same day
  const validateSchedule = () => {
    const errors = [];

    formData.schedule.forEach((s, index) => {
      // Check time order
      if (s.startTime >= s.endTime) {
        errors.push({ index, type: 'time', message: '시작 시간이 종료 시간보다 빠르거나 같습니다' });
      }

      // Check for overlapping times on same day
      formData.schedule.forEach((other, otherIndex) => {
        if (index < otherIndex && s.day === other.day) {
          if (timesOverlap(s.startTime, s.endTime, other.startTime, other.endTime)) {
            errors.push({ index, type: 'overlap', message: `${s.day}요일 ${other.startTime}-${other.endTime}와 시간이 겹칩니다` });
          }
        }
      });
    });

    setScheduleErrors(errors);
    return errors.length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateSchedule()) {
      alert('수업 시간 설정을 확인해주세요');
      return;
    }

    try {
      if (editingClass) {
        await api.put(`/api/class-groups/${editingClass._id}`, formData);
      } else {
        await api.post('/api/class-groups', formData);
      }
      setShowModal(false);
      loadClasses();
    } catch (error) {
      alert(error.response?.data?.error || '저장에 실패했습니다');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    try {
      await api.delete(`/api/class-groups/${id}`);
      loadClasses();
    } catch (error) {
      alert('삭제에 실패했습니다');
    }
  };

  const addSchedule = () => {
    setFormData({
      ...formData,
      schedule: [...formData.schedule, { day: '월', startTime: '14:00', endTime: '16:00' }]
    });
    setScheduleErrors([]);
  };

  const updateSchedule = (index, field, value) => {
    const newSchedule = [...formData.schedule];
    newSchedule[index][field] = value;
    setFormData({ ...formData, schedule: newSchedule });
    setScheduleErrors([]);
  };

  const removeSchedule = (index) => {
    setFormData({
      ...formData,
      schedule: formData.schedule.filter((_, i) => i !== index)
    });
    setScheduleErrors([]);
  };

  const getScheduleError = (index) => {
    return scheduleErrors.filter(e => e.index === index);
  };

  // Student management
  const handleAddStudent = async (studentId) => {
    // 정원 초과 체크
    const currentCount = selectedClass.students?.length || 0;
    if (currentCount >= selectedClass.maxStudents) {
      if (!window.confirm(`정원(${selectedClass.maxStudents}명)을 초과합니다. 그래도 추가하시겠습니까?`)) {
        return;
      }
    }

    try {
      await api.post(`/api/class-groups/${selectedClass._id}/students`, { studentIds: [studentId] });
      loadClasses();
      // Update selected class
      const updated = await api.get(`/api/class-groups/${selectedClass._id}`);
      setSelectedClass(updated.data.classGroup);
    } catch (error) {
      alert('학생 추가에 실패했습니다');
    }
  };

  const handleRemoveStudent = async (studentId) => {
    try {
      await api.delete(`/api/class-groups/${selectedClass._id}/students/${studentId}`);
      loadClasses();
      const updated = await api.get(`/api/class-groups/${selectedClass._id}`);
      setSelectedClass(updated.data.classGroup);
    } catch (error) {
      alert('학생 제거에 실패했습니다');
    }
  };

  const isStudentInClass = (studentId) => {
    if (!selectedClass?.students) return false;
    return selectedClass.students.some(s => (s._id || s) === studentId);
  };

  // Teacher management
  const handleAddTeacher = async (teacherId) => {
    try {
      const currentTeachers = selectedClass.teachers?.map(t => t._id || t) || [];
      await api.put(`/api/class-groups/${selectedClass._id}`, {
        teachers: [...currentTeachers, teacherId]
      });
      loadClasses();
      const updated = await api.get(`/api/class-groups/${selectedClass._id}`);
      setSelectedClass(updated.data.classGroup);
    } catch (error) {
      alert('강사 추가에 실패했습니다');
    }
  };

  const handleRemoveTeacher = async (teacherId) => {
    try {
      const currentTeachers = selectedClass.teachers?.map(t => t._id || t) || [];
      await api.put(`/api/class-groups/${selectedClass._id}`, {
        teachers: currentTeachers.filter(id => id !== teacherId)
      });
      loadClasses();
      const updated = await api.get(`/api/class-groups/${selectedClass._id}`);
      setSelectedClass(updated.data.classGroup);
    } catch (error) {
      alert('강사 제거에 실패했습니다');
    }
  };

  const isTeacherInClass = (teacherId) => {
    if (!selectedClass?.teachers) return false;
    return selectedClass.teachers.some(t => (t._id || t) === teacherId);
  };

  const getTeacherNames = (cls) => {
    if (!cls.teachers || cls.teachers.length === 0) return null;
    return cls.teachers.map(t => t.name || '(이름 없음)').join(', ');
  };

  // Excel Upload with xlsx support
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadFile(file);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        // 헤더 행 찾기 ('반이름' 포함된 행)
        let headerRowIndex = jsonData.findIndex(row =>
          row.some(cell => String(cell).includes('반이름'))
        );
        if (headerRowIndex === -1) headerRowIndex = 0;

        if (jsonData.length <= headerRowIndex + 1) {
          setUploadPreview([]);
          return;
        }

        const headers = jsonData[headerRowIndex].map(h => String(h || '').trim());
        const preview = [];
        for (let i = headerRowIndex + 1; i < Math.min(jsonData.length, headerRowIndex + 6); i++) {
          const row = {};
          headers.forEach((h, idx) => {
            if (h) row[h] = jsonData[i]?.[idx] || '';
          });
          // 빈 행 건너뛰기
          if (Object.values(row).some(v => v)) {
            preview.push(row);
          }
        }
        setUploadPreview(preview);
      } catch (err) {
        console.error('Failed to parse file:', err);
        alert('파일을 읽는데 실패했습니다');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const parseScheduleFromString = (scheduleStr) => {
    if (!scheduleStr) return [];

    const schedules = [];
    // 쉼표로 분리 (공백 유무 상관없이)
    const parts = String(scheduleStr).split(/[,，]\s*/).map(s => s.trim());

    parts.forEach(part => {
      // 더 유연한 정규식: 다양한 공백, 콜론(:, ：), 대시(-, –, —, ~) 지원
      // 정규화: 모든 공백, 특수문자를 표준화
      const normalized = part
        .replace(/\s+/g, ' ')           // 여러 공백을 하나로
        .replace(/：/g, ':')            // 전각 콜론을 반각으로
        .replace(/[–—～~]/g, '-')       // 다양한 대시를 하이픈으로
        .replace(/\s*:\s*/g, ':')       // 콜론 주변 공백 제거
        .replace(/\s*-\s*/g, '-')       // 하이픈 주변 공백 제거
        .trim();

      // 형식: 월14:00-16:00 또는 월 14:00-16:00
      const match = normalized.match(/([월화수목금토일])\s*(\d{1,2}:\d{2})-(\d{1,2}:\d{2})/);
      if (match) {
        schedules.push({
          day: match[1],
          startTime: match[2].padStart(5, '0'),
          endTime: match[3].padStart(5, '0')
        });
      }
    });

    return schedules;
  };

  // 엑셀 업로드용 시간 겹침 검사
  const validateScheduleArray = (schedules) => {
    for (let i = 0; i < schedules.length; i++) {
      const s = schedules[i];
      // 시작 시간이 종료 시간보다 늦으면 에러
      if (s.startTime >= s.endTime) {
        return { valid: false, error: `${s.day}요일: 시작 시간이 종료 시간보다 늦습니다` };
      }
      // 같은 요일에 시간이 겹치면 에러
      for (let j = i + 1; j < schedules.length; j++) {
        const other = schedules[j];
        if (s.day === other.day && timesOverlap(s.startTime, s.endTime, other.startTime, other.endTime)) {
          return { valid: false, error: `${s.day}요일 시간이 겹칩니다` };
        }
      }
    }
    return { valid: true };
  };

  const handleUpload = async () => {
    if (!uploadFile) return;

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];

          // 전체 데이터를 배열로 읽기
          const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          // 헤더 행 찾기 ('반이름' 포함된 행)
          let headerRowIndex = rawData.findIndex(row =>
            row.some(cell => String(cell).includes('반이름'))
          );
          if (headerRowIndex === -1) headerRowIndex = 0;

          // 헤더 이후 데이터를 JSON으로 변환
          const headers = rawData[headerRowIndex];
          const jsonData = [];
          for (let i = headerRowIndex + 1; i < rawData.length; i++) {
            const row = rawData[i];
            if (!row || row.length === 0) continue;
            const obj = {};
            headers.forEach((h, idx) => {
              if (h) obj[String(h).trim()] = row[idx];
            });
            jsonData.push(obj);
          }

          let successCount = 0;
          let failCount = 0;

          const errors = [];

          for (const row of jsonData) {
            const name = row['반이름'] || row['name'] || row['Name'];
            if (!name) continue;

            try {
              const schedule = parseScheduleFromString(row['수업시간'] || row['schedule'] || '');

              // 시간 겹침 검사
              const validation = validateScheduleArray(schedule);
              if (!validation.valid) {
                errors.push(`${name}: ${validation.error}`);
                failCount++;
                continue;
              }

              await api.post('/api/class-groups', {
                name,
                subject: row['과목'] || row['subject'] || '',
                grade: row['학년'] || row['grade'] || '',
                maxStudents: parseInt(row['최대인원'] || row['maxStudents']) || 20,
                tuitionFee: parseInt(row['월수강료'] || row['tuitionFee']) || 0,
                schedule
              });
              successCount++;
            } catch (err) {
              console.error('Failed to create class:', err);
              errors.push(`${name}: ${err.response?.data?.error || err.message}`);
              failCount++;
            }
          }

          // 결과 저장 및 표시
          setUploadResult({
            success: successCount,
            failed: failCount,
            errors
          });

          // 성공만 있으면 모달 닫기
          if (errors.length === 0) {
            alert(`업로드 완료: 성공 ${successCount}개`);
            setShowUploadModal(false);
            setUploadFile(null);
            setUploadPreview([]);
          }
          loadClasses();
        } catch (err) {
          alert('파일 처리에 실패했습니다');
        }
      };
      reader.readAsArrayBuffer(uploadFile);
    } catch (error) {
      alert('업로드에 실패했습니다');
    }
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ['[반 일괄 등록 양식]'],
      ['※ 작성 안내'],
      ['- 반이름: 필수 입력'],
      ['- 과목: 수학, 영어, 국어 등'],
      ['- 학년: 초1~초6, 중1~중3, 고1~고3 또는 범위(예: 중1-3)'],
      ['- 최대인원: 숫자만 입력 (기본값: 20)'],
      ['- 월수강료: 숫자만 입력 (예: 300000)'],
      ['- 수업시간: 요일 시작시간-종료시간 형식, 여러 개는 쉼표로 구분'],
      ['  예) 월 14:00-16:00 또는 월 14:00-16:00, 수 14:00-16:00'],
      [''],
      ['반이름', '과목', '학년', '최대인원', '월수강료', '수업시간'],
      ['수학A반', '수학', '중2', 20, 300000, '월 14:00-16:00'],
      ['영어B반', '영어', '중1-3', 15, 250000, '화 15:00-17:00, 목 15:00-17:00'],
      ['국어심화반', '국어', '고1', 10, 400000, '월 18:00-20:00, 수 18:00-20:00, 금 18:00-20:00'],
      ['초등수학', '수학', '초5-6', 12, 200000, '토 10:00-12:00']
    ]);

    // 컬럼 너비 설정
    ws['!cols'] = [
      { wch: 15 },  // 반이름
      { wch: 10 },  // 과목
      { wch: 10 },  // 학년
      { wch: 10 },  // 최대인원
      { wch: 12 },  // 월수강료
      { wch: 50 }   // 수업시간
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '반목록');
    XLSX.writeFile(wb, '반_업로드_양식.xlsx');
  };

  return (
    <div className="classes-page">
      <div className="page-header">
        <h2>반 관리</h2>
        {isAdmin && (
          <div className="header-actions">
            <button className="btn btn-secondary" onClick={() => { setUploadResult(null); setShowUploadModal(true); }}>
              <FiUpload /> 엑셀 업로드
            </button>
            <button className="btn btn-primary" onClick={() => openModal()}>
              <FiPlus /> 반 추가
            </button>
          </div>
        )}
      </div>

      {/* Class Cards */}
      <div className="class-grid">
        {loading ? (
          <p className="loading">로딩 중...</p>
        ) : classes.length === 0 ? (
          <p className="empty">등록된 반이 없습니다</p>
        ) : (
          classes.map((cls) => (
            <div key={cls._id} className="class-card">
              <div className="class-header">
                <h3>{cls.name}</h3>
                <div className="class-actions">
                  {isAdmin && (
                    <>
                      {!isSoloMode && (
                        <button className="icon-btn" onClick={() => openTeachersModal(cls)} title="강사 관리">
                          <FiUserCheck />
                        </button>
                      )}
                      <button className="icon-btn" onClick={() => openStudentsModal(cls)} title="학생 관리">
                        <FiUserPlus />
                      </button>
                      <button className="icon-btn" onClick={() => openModal(cls)} title="수정">
                        <FiEdit2 />
                      </button>
                      <button className="icon-btn danger" onClick={() => handleDelete(cls._id)} title="삭제">
                        <FiTrash2 />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="class-info">
                {cls.subject && <span className="tag">{cls.subject}</span>}
                {cls.grade && <span className="tag">{cls.grade}</span>}
              </div>
              {getTeacherNames(cls) && (
                <div className="class-teachers">
                  <FiUserCheck /> {getTeacherNames(cls)}
                </div>
              )}
              <div className="class-schedule">
                {Array.isArray(cls.schedule) && cls.schedule.map((s, i) => (
                  <span key={i} className="schedule-item">
                    {s.day} {s.startTime}-{s.endTime}
                  </span>
                ))}
              </div>
              <div className="class-footer">
                <span className="student-count">
                  <FiUsers /> {cls.students?.length || 0} / {cls.maxStudents}명
                </span>
                <span className={`status ${cls.isActive ? 'active' : 'inactive'}`}>
                  {cls.isActive ? '운영중' : '중단'}
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingClass ? '반 수정' : '반 추가'}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>반 이름 *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="예: 수학 A반"
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>과목</label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="예: 수학"
                  />
                </div>
                <div className="form-group">
                  <label>대상 학년</label>
                  <input
                    type="text"
                    value={formData.grade}
                    onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                    placeholder="예: 중2-3"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>최대 인원</label>
                  <input
                    type="number"
                    value={formData.maxStudents}
                    onChange={(e) => setFormData({ ...formData, maxStudents: parseInt(e.target.value) })}
                    min="1"
                    max="100"
                  />
                </div>
                <div className="form-group">
                  <label>월 수강료</label>
                  <input
                    type="number"
                    value={formData.tuitionFee || ''}
                    onChange={(e) => setFormData({ ...formData, tuitionFee: parseInt(e.target.value) || 0 })}
                    min="0"
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Schedule */}
              <div className="form-group">
                <label>수업 시간</label>
                {formData.schedule.map((s, index) => {
                  const errors = getScheduleError(index);
                  return (
                    <div key={index}>
                      <div className={`schedule-row ${errors.length > 0 ? 'has-error' : ''}`}>
                        <select
                          value={s.day}
                          onChange={(e) => updateSchedule(index, 'day', e.target.value)}
                        >
                          {days.map((d) => (
                            <option key={d} value={d}>{d}</option>
                          ))}
                        </select>
                        <input
                          type="time"
                          value={s.startTime}
                          onChange={(e) => updateSchedule(index, 'startTime', e.target.value)}
                        />
                        <span>~</span>
                        <input
                          type="time"
                          value={s.endTime}
                          onChange={(e) => updateSchedule(index, 'endTime', e.target.value)}
                        />
                        <button type="button" className="icon-btn danger" onClick={() => removeSchedule(index)}>
                          <FiX />
                        </button>
                      </div>
                      {errors.length > 0 && (
                        <div className="schedule-errors">
                          {errors.map((err, i) => (
                            <span key={i} className="error-text">{err.message}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                <button type="button" className="btn btn-secondary btn-sm" onClick={addSchedule}>
                  + 시간 추가
                </button>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  취소
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingClass ? '수정' : '추가'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Students Modal */}
      {showStudentsModal && selectedClass && (
        <div className="modal-overlay" onClick={() => setShowStudentsModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedClass.name} - 학생 관리</h3>
              <button className="close-btn" onClick={() => setShowStudentsModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <div className="students-section">
                <h4>배정된 학생 ({selectedClass.students?.length || 0}명)</h4>
                <div className="student-list">
                  {(!selectedClass.students || selectedClass.students.length === 0) ? (
                    <p className="text-muted">배정된 학생이 없습니다</p>
                  ) : (
                    selectedClass.students.map((student) => (
                      <div key={student._id || student} className="student-item">
                        <span>{student.name || '(이름 없음)'}</span>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleRemoveStudent(student._id || student)}
                        >
                          제거
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <hr />

              <div className="students-section">
                <h4>학생 추가</h4>
                <div className="student-list">
                  {students
                    .filter(s => s.isActive && !isStudentInClass(s._id))
                    .sort((a, b) => {
                      // 반의 대상 학년과 매칭되는 학생 우선 표시
                      const classGrade = selectedClass.grade || '';
                      const aGrade = a.studentInfo?.grade || '';
                      const bGrade = b.studentInfo?.grade || '';

                      // 학년이 반의 대상 학년에 포함되면 우선
                      const aMatch = classGrade && aGrade && classGrade.includes(aGrade);
                      const bMatch = classGrade && bGrade && classGrade.includes(bGrade);

                      if (aMatch && !bMatch) return -1;
                      if (!aMatch && bMatch) return 1;

                      // 둘 다 매칭되거나 안되면 이름순
                      return a.name.localeCompare(b.name, 'ko');
                    })
                    .map((student) => {
                      const classGrade = selectedClass.grade || '';
                      const studentGrade = student.studentInfo?.grade || '';
                      const isMatch = classGrade && studentGrade && classGrade.includes(studentGrade);

                      return (
                        <div key={student._id} className={`student-item ${isMatch ? 'grade-match' : ''}`}>
                          <span>
                            {student.name}
                            {student.studentInfo?.grade && (
                              <small className={isMatch ? 'text-primary' : 'text-muted'}> ({student.studentInfo.grade})</small>
                            )}
                          </span>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleAddStudent(student._id)}
                          >
                            추가
                          </button>
                        </div>
                      );
                    })
                  }
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowStudentsModal(false)}>
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Teachers Modal */}
      {showTeachersModal && selectedClass && (
        <div className="modal-overlay" onClick={() => setShowTeachersModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedClass.name} - 강사 관리</h3>
              <button className="close-btn" onClick={() => setShowTeachersModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <div className="students-section">
                <h4>배정된 강사 ({selectedClass.teachers?.length || 0}명)</h4>
                <div className="student-list">
                  {(!selectedClass.teachers || selectedClass.teachers.length === 0) ? (
                    <p className="text-muted">배정된 강사가 없습니다</p>
                  ) : (
                    selectedClass.teachers.map((teacher) => (
                      <div key={teacher._id || teacher} className="student-item">
                        <span>
                          <FiUserCheck style={{ marginRight: '8px' }} />
                          {teacher.name || '(이름 없음)'}
                        </span>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleRemoveTeacher(teacher._id || teacher)}
                        >
                          제거
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <hr />

              <div className="students-section">
                <h4>강사 추가</h4>
                <div className="student-list">
                  {teachers
                    .filter(t => t.isActive && !isTeacherInClass(t._id))
                    .map((teacher) => (
                      <div key={teacher._id} className="student-item">
                        <span>
                          <FiUserCheck style={{ marginRight: '8px' }} />
                          {teacher.name}
                          {teacher.email && <small className="text-muted"> ({teacher.email})</small>}
                        </span>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleAddTeacher(teacher._id)}
                        >
                          추가
                        </button>
                      </div>
                    ))
                  }
                  {teachers.filter(t => t.isActive && !isTeacherInClass(t._id)).length === 0 && (
                    <p className="text-muted">추가할 수 있는 강사가 없습니다</p>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowTeachersModal(false)}>
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>엑셀 업로드</h3>
              <button className="close-btn" onClick={() => setShowUploadModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="upload-content">
              <div className="upload-info">
                <p>엑셀 파일(.xlsx)로 반을 일괄 등록할 수 있습니다.</p>
                <p className="text-muted">수업시간 형식: 월 14:00-16:00 (여러 개는 쉼표로 구분)</p>
                <button className="btn btn-outline btn-sm" onClick={downloadTemplate}>
                  <FiDownload /> 양식 다운로드
                </button>
              </div>

              <div className="upload-area">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <button
                  className="btn btn-secondary"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FiUpload /> 파일 선택
                </button>
                {uploadFile && (
                  <span className="file-name">{uploadFile.name}</span>
                )}
              </div>

              {uploadPreview.length > 0 && (
                <div className="upload-preview">
                  <h4>미리보기 (최대 5건)</h4>
                  <table>
                    <thead>
                      <tr>
                        {Object.keys(uploadPreview[0]).map(key => (
                          <th key={key}>{key}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {uploadPreview.map((row, i) => (
                        <tr key={i}>
                          {Object.values(row).map((val, j) => (
                            <td key={j}>{val}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* 업로드 결과 표시 */}
              {uploadResult && (
                <div className="upload-result">
                  <div className="upload-summary">
                    <span className="success-count">성공: {uploadResult.success}개</span>
                    {uploadResult.failed > 0 && (
                      <span className="fail-count">실패: {uploadResult.failed}개</span>
                    )}
                  </div>
                  {uploadResult.errors.length > 0 && (
                    <div className="upload-errors">
                      <h5>실패 사유:</h5>
                      <ul>
                        {uploadResult.errors.map((err, idx) => (
                          <li key={idx}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowUploadModal(false)}>
                  취소
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleUpload}
                  disabled={!uploadFile}
                >
                  업로드
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
